import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import TextBoxViewModel from '@/ViewModels/Controls/TextBoxViewModel';
import LinkViewModelBuilder from '@/Builder/LinkViewModelBuilder';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import NumericTextBoxViewModel from '@/ViewModels/Controls/NumericTextBoxViewModel';
import { Dictionary } from '#/DefaultViewExtensionInterfaces';
import ResourceManager from '@/Management/ResourceManager';
import CrontabWindowViewModel from '@/ViewModels/Controls/CrontabWindowViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class CrontabViewModel extends ControlViewModel {
    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModel>('TextBoxViewModel');
    private NumericTextBoxViewModel = ComponentManager.getViewModel<typeof NumericTextBoxViewModel>('NumericTextBoxViewModel');
    private CrontabWindowViewModel = ComponentManager.getViewModel<typeof CrontabWindowViewModel>('CrontabWindowViewModel');

    public patternValues: Array<string>;
    public value: KnockoutObservable<string>;
    public name: KnockoutObservable<string>;
    public onChange: (value: string) => void;
    public link: LinkViewModel
    public textBoxMain: TextBoxViewModel;
    public textBoxWindow: TextBoxViewModel;
    public cronJobBoxes: Array<NumericTextBoxViewModel>;
    public cronJobWindow: CrontabWindowViewModel;
    
    public constructor(protected params: any) {
        super(params);
        this.patternValues = ['A', 'M', 'W', 'd', 'h', 'm', 's'];
        this.value = this.observableOrDefault(params.value, '');
        this.name = this.observableOrDefault(params.name);
        this.onChange = this.handlerOrDefault(params.onChange);
        this.textBoxWindow = this.paramOrDefault(params.textBox, null);
        this.link = this.createLink();
        this.textBoxMain = this.createTextBox.bind(this);
        this.cronJobBoxes = this.createCrontabInputFields();
        
        this.cronJobWindow = this.createWindow();
        this.validateState();
    }

    /**
     * creates the main-control textbox
     */
    private createTextBox(): TextBoxViewModel {
        return new this.TextBoxViewModel({
            value: this.value,
            isValid: this.isValid,
            isEnabled: false,
            isFocusable: this.isFocusable,
            invalidationText: this.invalidationText,
            locator: LocatorHelper.buildLocator(this.locator, 'patternValue')
        });
    }

    /**
     * creates the WindowViewModel for the popup
     */
    private createWindow(): CrontabWindowViewModel {
        return new this.CrontabWindowViewModel({
            width: 650,
            isModal: true,
            isOpen: false,
            isVible: true,
            allowClose: true,
            isDraggable: false,
            isResizable: false,
            headline: new LabelViewModelBuilder()
                .withErgName('BACKGROUND_PROCESS_CRONTAB_PATTERN_WINDOW_TITLE')
                .build()
            ,
            onOpen: () => {
                this.fillTextBoxesOnOpenWindow();
            },
            onClose: () => {
                this.resetTextBoxesOnCloseWindow();
            },
            centerOnWindowResize: true,
            locator: LocatorHelper.buildLocator(this.locator, 'window'),
            value: this.value,
            patternValues: this.patternValues,
            cronJobBoxes: this.cronJobBoxes
        });
    }

    /**
     * clears the textboxes, if the window gets closed
     */
    private resetTextBoxesOnCloseWindow(): void {
        this.cronJobBoxes.forEach(box => box.resetValue());
    }

    /**
     * fills the textboxes, if the window gets opened
     */ 
    private fillTextBoxesOnOpenWindow(): void {
        if (!String.isNullOrEmpty(this.value())) {
            let actualValue = this.value().split(',');
            for (let i = 0; i < actualValue.length; i++) {
                let matchPattern = actualValue[i].match('(([0-9]+)([A|M|W|d|h|m|s]))');

                if (matchPattern == null)
                    return;

                let caption = matchPattern[3];
                let valueToSet = parseInt(matchPattern[2]);
                let numericTextBoxFiltered = this.cronJobBoxes.filter(numeric => numeric.label.caption.peek() == caption);
                let numericTextBox = numericTextBoxFiltered[0];
                numericTextBoxFiltered[0].value(matchPattern[2]);
                if (valueToSet > numericTextBox.max()) {
                    numericTextBox.invalidationText(ResourceManager.getInstance().get('BACKGROUND_PROCESS_CRONTAB_PATTERN_TOOLTIP_INVALID_VALUE')
                        .replace('%oldValue%', valueToSet.toString())
                        .replace('%maxValue%', numericTextBox.max().toString()));
                }
            }
        }
    }

    /**
     * Creates all NumericTextBoxViewModels for the input fields in the window 
     */
    private createCrontabInputFields(): Array<NumericTextBoxViewModel> {
        let list = [];

        let availableFields = this.patternValues

        let ergNameKeys = this.getTooltipErgNameForPatternValue();
        let maxValueKeys = this.getMaxValueForPatternValue();

        for (let i = 0; i < availableFields.length; i++) {
            let ergName = ergNameKeys[availableFields[i]];
            let maxVal = maxValueKeys[availableFields[i]];
            let box = this.createCrontabInputField(availableFields[i], maxVal, ergName);

            list.push(box);
        }

        return list;
    }

    /**
     * Creates a single NumericTextBoxViewModel for the window
     */
    private createCrontabInputField(patternValue: string, maxValue: number, tooltipValue: string): NumericTextBoxViewModel {
        return new this.NumericTextBoxViewModel({
            label: new LabelViewModelBuilder()
                .withCaption(patternValue)
                .build(),
            isValid: true,
            isEnabled: this.isEnabled,
            isFocusable: this.isFocusable,
            locator: LocatorHelper.buildLocator(this.locator, patternValue),
            floatingPrecision: 0,
            min: 0,
            max: maxValue,
            showSpinners: true,
            tooltip: new LabelViewModelBuilder()
                .withErgName(tooltipValue)
                .build()
        });
    }

    /**
     * returns the ergName-Tooltip for a property
     */
    private getTooltipErgNameForPatternValue(): Dictionary<string> {
        let prefix = 'BACKGROUND_PROCESS_CRONTAB_QUICKSELECT_TOOLTIP';

        return {
            'A': `${prefix}_YEAR`,
            'M': `${prefix}_MONTH`,
            'W': `${prefix}_WEEK`,
            'd': `${prefix}_DAY`,
            'h': `${prefix}_HOUR`,
            'm': `${prefix}_MINUTE`,
            's': `${prefix}_SECONDS`,
        };
    }

    /**
     * returns the max-value for a property
     */
    private getMaxValueForPatternValue(): Dictionary<number> {
        return {
            'A': Number.MAX_VALUE,
            'M': Number.MAX_VALUE,
            'W': 52,
            'd': 365,
            'h': 24,
            'm': 60,
            's': 60,
        };
    }

    /**
     * creates a LinkViewModel for opening the window
     */
    private createLink(): LinkViewModel {
        return new LinkViewModelBuilder()
            .withIsEnabled(this.isEnabled)
            .withLocator(LocatorHelper.buildLocator(this.locator, 'patternWindow'))
            .withImage()
            .withSource(GlobalConfiguration.inlineImages.TIMEPICKER)
            .done()
            .withTooltip()
            .withErgName('BACKGROUND_PROCESS_CRONTAB_PATTERN_TOOLTIP_OPEN_WINDOW')
            .done()
            .withIsToolBarLink(true)
            .withOnClick(() => {
                this.cronJobWindow.isOpen(true);
            })
            .build();
    }

    private validateState(): void {
        this.cronJobBoxes.forEach(box => box.onChange = () => {
            box.isValid(!(parseInt(box.value()) > box.max()))
            box.invalidationText(null);
        });
    }

    public validate(value: string): boolean {
        let regexPattern = '(([0-9]+)([A|M|W|d|h|m|s]))'

        if (String.isNullOrEmpty(value)) {
            return true;
        }

        let valueSplit = value.split(',');

        for (let i = 0; i < valueSplit.length; i++) {
            let matchPattern = valueSplit[i].match(regexPattern);

            if (matchPattern == null) {
                return false;
            }

            let caption = matchPattern[3];
            let valueToSet = parseInt(matchPattern[2]);

            let maxLength = this.getMaxValueForPatternValue()[caption];
            if (valueToSet > maxLength) {
                return false;
            }
        }

        return true;
    }
}

ComponentManager.setViewModel('CrontabViewModel', CrontabViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');